import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from './ui/button';
import { CheckCircle, ChevronLeft, Pen, Globe } from 'lucide-react';

// Translations
const translations = {
  en: {
    title: 'Treatment Consent Form',
    patient: 'Patient',
    date: 'Date',
    services: 'Services',
    medications: 'Medications',
    description: 'Description',
    qty: 'Qty',
    unitPrice: 'Unit Price',
    amount: 'Amount',
    subtotal: 'Subtotal',
    totalAmountDue: 'Total Amount Due',
    consentText: (name, total) => 
      `I, ${name}, acknowledge that I have reviewed and understand the services provided and medications dispensed as listed above. I agree to pay the total amount of $${total}.`,
    patientSignature: 'Patient Signature',
    clear: 'Clear',
    signatureHint: 'Please sign above using your finger or stylus',
    signatureCaptured: 'Signature captured',
    cancel: 'Cancel',
    pleaseAgree: 'Please agree to terms',
    pleaseSign: 'Please sign above',
    confirmSubmit: 'Confirm & Submit for Billing',
    language: 'Language'
  },
  es: {
    title: 'Formulario de Consentimiento de Tratamiento',
    patient: 'Paciente',
    date: 'Fecha',
    services: 'Servicios',
    medications: 'Medicamentos',
    description: 'Descripción',
    qty: 'Cant.',
    unitPrice: 'Precio Unit.',
    amount: 'Importe',
    subtotal: 'Subtotal',
    totalAmountDue: 'Monto Total a Pagar',
    consentText: (name, total) => 
      `Yo, ${name}, reconozco que he revisado y entiendo los servicios proporcionados y los medicamentos dispensados según se indica arriba. Acepto pagar el monto total de $${total}.`,
    patientSignature: 'Firma del Paciente',
    clear: 'Borrar',
    signatureHint: 'Por favor firme arriba usando su dedo o stylus',
    signatureCaptured: 'Firma capturada',
    cancel: 'Cancelar',
    pleaseAgree: 'Por favor acepte los términos',
    pleaseSign: 'Por favor firme arriba',
    confirmSubmit: 'Confirmar y Enviar a Facturación',
    language: 'Idioma'
  }
};

const ConsentForm = ({ 
  patientName, 
  services = [], 
  medications = [], 
  onConsent,
  onCancel 
}) => {
  const sigCanvas = useRef(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [language, setLanguage] = useState('en');

  const t = translations[language];

  // Calculate totals
  const servicesTotal = services.reduce((sum, s) => sum + (s.total || s.quantity * s.unit_price || 0), 0);
  const medicationsTotal = medications.reduce((sum, m) => sum + (m.total || m.quantity * m.unit_price || 0), 0);
  const grandTotal = servicesTotal + medicationsTotal;

  const clearSignature = () => {
    sigCanvas.current?.clear();
    setHasSignature(false);
  };

  const handleSignatureEnd = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setHasSignature(true);
    }
  };

  const handleSubmit = () => {
    if (!hasSignature || !agreed) return;
    
    const signatureData = sigCanvas.current.toDataURL('image/png');
    const consentData = {
      signature: signatureData,
      timestamp: new Date().toISOString(),
      language: language,
      services_acknowledged: services.map(s => ({ name: s.service_name, quantity: s.quantity, total: s.total })),
      medications_acknowledged: medications.map(m => ({ name: m.medication_name, quantity: m.quantity, total: m.total })),
      total_acknowledged: grandTotal
    };
    
    onConsent(consentData);
  };

  const handleCancel = () => {
    setHasSignature(false);
    setAgreed(false);
    sigCanvas.current?.clear();
    onCancel();
  };

  const formatDate = () => {
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return new Date().toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', options);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="consent-form-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          <button 
            onClick={handleCancel}
            className="flex items-center gap-1 text-gray-600 hover:text-gray-900"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm font-medium">{t.cancel}</span>
          </button>
          
          <h1 className="text-lg font-semibold text-gray-900">{t.title}</h1>
          
          {/* Language Selector */}
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-gray-500" />
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="language-selector"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6 pb-32">
        {/* Patient Info */}
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500">{t.patient}</p>
              <p className="text-xl font-semibold text-gray-900">{patientName}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">{t.date}</p>
              <p className="text-sm text-gray-900">{formatDate()}</p>
            </div>
          </div>
        </div>

        {/* Services Table */}
        {services.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
              {t.services}
            </h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-2 font-medium text-gray-600">{t.description}</th>
                    <th className="text-center px-4 py-2 font-medium text-gray-600 w-16">{t.qty}</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600 w-24">{t.unitPrice}</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600 w-24">{t.amount}</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((service, idx) => (
                    <tr key={idx} className="border-b border-gray-100 last:border-0">
                      <td className="px-4 py-3 text-gray-900">{service.service_name}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{service.quantity}</td>
                      <td className="px-4 py-3 text-right text-gray-600">${(service.unit_price || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">${(service.total || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50">
                    <td colSpan="3" className="px-4 py-2 text-right font-medium text-gray-700">{t.subtotal}</td>
                    <td className="px-4 py-2 text-right font-semibold text-gray-900">${servicesTotal.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Medications Table */}
        {medications.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
              {t.medications}
            </h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-2 font-medium text-gray-600">{t.description}</th>
                    <th className="text-center px-4 py-2 font-medium text-gray-600 w-16">{t.qty}</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600 w-24">{t.unitPrice}</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600 w-24">{t.amount}</th>
                  </tr>
                </thead>
                <tbody>
                  {medications.map((med, idx) => (
                    <tr key={idx} className="border-b border-gray-100 last:border-0">
                      <td className="px-4 py-3 text-gray-900">{med.medication_name}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{med.quantity}</td>
                      <td className="px-4 py-3 text-right text-gray-600">${(med.unit_price || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">${(med.total || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50">
                    <td colSpan="3" className="px-4 py-2 text-right font-medium text-gray-700">{t.subtotal}</td>
                    <td className="px-4 py-2 text-right font-semibold text-gray-900">${medicationsTotal.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Grand Total */}
        <div className="mb-8 p-4 bg-blue-100 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between text-blue-800">
            <span className="font-medium">{t.totalAmountDue}</span>
            <span className="text-2xl font-bold">${grandTotal.toFixed(2)}</span>
          </div>
        </div>

        {/* Consent Agreement */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              data-testid="consent-checkbox"
            />
            <span className="text-sm text-gray-700 leading-relaxed">
              {t.consentText(patientName, grandTotal.toFixed(2))}
            </span>
          </label>
        </div>

        {/* Signature Pad */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Pen className="w-4 h-4 text-gray-600" />
              <span className="font-medium text-gray-700">{t.patientSignature}</span>
            </div>
            <button
              type="button"
              onClick={clearSignature}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {t.clear}
            </button>
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 p-2">
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              canvasProps={{
                className: 'w-full h-40 rounded-lg bg-white'
              }}
              onEnd={handleSignatureEnd}
            />
          </div>
          {!hasSignature && (
            <p className="text-xs text-gray-500 text-center mt-2">
              {t.signatureHint}
            </p>
          )}
          {hasSignature && (
            <p className="text-xs text-green-600 text-center mt-2 flex items-center justify-center gap-1">
              <CheckCircle className="w-3 h-3" />
              {t.signatureCaptured}
            </p>
          )}
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-20">
        <div className="max-w-3xl mx-auto flex gap-3">
          <Button 
            variant="outline" 
            onClick={handleCancel} 
            className="flex-1 h-12"
          >
            {t.cancel}
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!hasSignature || !agreed}
            className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="submit-consent-btn"
          >
            {!agreed ? t.pleaseAgree : !hasSignature ? t.pleaseSign : t.confirmSubmit}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConsentForm;
